import { EditableComponent, EditableComponentProps } from '@adobe/aem-react-editable-components';
import React, { FC, useMemo, useState, useCallback, useEffect, useRef, useId, startTransition } from 'react';
import { useDCAPageModelStore, useClientEnvVarsStore } from '@marriott/mi-store-utils';
import clsx from 'clsx';
import { renditions } from '@marriott/mi-ui-library';
import {
  DcaNode,
  DynamicTabbedLayoutCompProps,
  DynamicTabbedLayoutProps,
  SlotContent,
  IPageModel,
  IReturnTypeOFGetMboxParam,
  CfNode,
  GeoNode,
  DcaResponseEntry,
  ExtendedCfNode,
} from './DynamicTabbedLayout.types';
import { TabNavigation } from '../../molecules/Tabs/TabNavigation';
import { TabBanner } from '../../molecules/Tabs/TabBanner';
import { StyledDynamicTabbedLayout, StyledTabPanelGrid, StyledTabPanelItem } from './DynamicTabbedLayout.styles';
import {
  buildPatternSlots,
  overlaySlotsWithDcaRes,
  overlaySlotsWithGeoRes,
  mergeCfAndDcaToSlots,
  normalizeCfItems,
  isDcaTargetPayloadFailed,
  mergeAlloyMboxResponses,
  resolveMboxResponses,
} from '../../utils/dynamicContentUtils';
import { dcaMockData } from './dcaMockContent.mock';
import { getDcaNonCobrandImpressionEntry, resolveSlotComponent } from '../../utils/resolveSlotComponent';
import { getMboxParamters, fetchDCADynamicModelForAlloy, useSession } from '@marriott/mi-headless-utils';
import { fetchCFData } from '../../utils/fetchCFData';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useGetUxlForDTL } from 'mi-homepage-graphql';
import { buildTabTrackingPrefix, resolveTabClickLocation } from '../../utils/polarisTrackingUtils';
import { resolveRegionFromState, selectCardsForRegion } from '../../utils/geoTargetingUtils';
import { FEATURE_FLAGS } from '@marriott/mi-feature-flag-utils';
import { useRetargetingCandidate } from '../../hooks/useRetargetingCandidate';
import { HOMEPAGE_HWS_RLM_USE_CASE } from '../../utils/retargeting/retargeting.constants';
import { resolveRetargetingSlot } from '../../utils/retargeting/retargetingRuntime';
import { RetargetingSlotErrorBoundary } from './RetargetingSlotErrorBoundary';
import { clearRetargetingImageFailureLogs } from './RetargetingProductDetailsCard';

/**
 * ACDL (Adobe Client Data Layer) key holding the visitor's Akamai-derived US
 * state, e.g. "va". The Homepage runs on ACDL, so client-side region detection
 * sources the state from here (window.adobeDataLayer.getState()).
 */
const ACDL_AKAMAI_STATE_KEY = 'browser_akamai_loc_state';

/**
 * Read the visitor's US state from the client data layer. Prefers ACDL
 * (window.adobeDataLayer.getState()) which is what the Homepage uses, and
 * falls back to the session datalayer for the non-ACDL path.
 */
function getClientState(sessionDatalayer: Record<string, unknown> | undefined): string | undefined {
  const acdlState =
    typeof window !== 'undefined' ? window.adobeDataLayer?.getState?.()?.[ACDL_AKAMAI_STATE_KEY] : undefined;
  const state = acdlState ?? sessionDatalayer?.[ACDL_AKAMAI_STATE_KEY];
  return typeof state === 'string' && state.length > 0 ? state : undefined;
}

const isMockCFDataEnabled = () => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const nextPublicMockCfData = process.env.NEXT_PUBLIC_USE_MOCK_CFDATA;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const mockCfData = process.env.USE_MOCK_CFDATA;
  return nextPublicMockCfData === 'true' || mockCfData === 'true';
};

const isMockDCADataEnabled = () => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const nextPublicMockDcaData = process.env.NEXT_PUBLIC_USE_MOCK_DCADATA;
  return nextPublicMockDcaData === 'true';
};

/** Returns mbox names that still need an Alloy fetch given current store state. */
function getMissingMboxes(scopes: string[], dcaResponseByMbox: Record<string, unknown>): string[] {
  return scopes.filter((mboxName): mboxName is string => {
    if (!mboxName) return false;
    const existing = dcaResponseByMbox[mboxName] as unknown as DcaResponseEntry | null | undefined;

    if (existing == null) return true;

    // DCA_MBOX_RESPONSE_EMPTY: When secondary prefetch gets no content for an mbox,
    // it stores { loadCfData: true } to indicate "use CF fallback". Don't re-fetch.
    if (existing?.loadCfData === true) return false;

    // DCA_MBOX_RESPONSE_FAILED: When a fetch fails (network error, malformed response),
    if (existing?.isTargetContentError === true) {
      return existing?.fromUxlPageModel !== true;
    }
    return false;
  });
}

function arraysToObjects(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.reduce<Record<string, unknown>>((acc, item, idx) => {
      acc[String(idx)] = arraysToObjects(item);
      return acc;
    }, {});
  }
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const next: Record<string, unknown> = {};
    Object.keys(obj).forEach(k => {
      next[k] = arraysToObjects(obj[k]);
    });
    return next;
  }
  return value;
}

/** Extract all DTL mboxes via getMboxParamters, then filter to only missing names. */
function buildAlloyPayloadMboxes(
  missingNames: string[],
  model: DynamicTabbedLayoutCompProps
): IReturnTypeOFGetMboxParam[] {
  if (missingNames.length === 0) return [];

  const modelCopy = arraysToObjects(JSON.parse(JSON.stringify(model))) as unknown as IPageModel;
  const { mboxParameters } = getMboxParamters(modelCopy);
  const allow = new Set(missingNames);
  const seen = new Set<string>();
  const payloadMboxes: IReturnTypeOFGetMboxParam[] = [];

  for (const item of (mboxParameters as IReturnTypeOFGetMboxParam[]) || []) {
    const name = String((item as { mboxName?: string } | undefined)?.mboxName ?? '');
    if (allow.has(name) && !seen.has(name)) {
      payloadMboxes.push(item);
      seen.add(name);
    }
  }

  return payloadMboxes;
}

/** Prevents infinite passive-effect loops when overlay yields identical slots (new array identity each run). */
function isSameTabSlotArray(prev: SlotContent[] | undefined, next: SlotContent[]): boolean {
  if (prev === next) return true;
  if (!prev || prev.length !== next.length) return false;
  try {
    return JSON.stringify(prev) === JSON.stringify(next);
  } catch {
    return false;
  }
}

const TabContainerConfig = {
  emptyLabel: 'Dynamic Tabbed Layout',
  isEmpty: () => true,
  resourceType: `mi-aem-homepage-spa/components/content/dynamictabbedlayout`,
};

export const RETARGETING_ELIGIBLE_USE_CASES = [HOMEPAGE_HWS_RLM_USE_CASE] as const;

export const DynamicTabbedLayout = (props: EditableComponentProps & DynamicTabbedLayoutProps) => (
  <EditableComponent config={{ ...TabContainerConfig }} {...props}>
    <DynamicTabbedLayoutComp {...props.model} isAuthorMode={props.isAuthorMode} />
  </EditableComponent>
);

const DynamicTabbedLayoutComp: FC<DynamicTabbedLayoutCompProps> = (model: DynamicTabbedLayoutCompProps) => {
  const {
    styleclass,
    targetComponentName,
    trackingProperties,
    tabDetails = [],
    pageTitle,
    requestedTab,
    requestedTabData,
    isAuthorMode = false,
    isNextGenSearchForm = false,
    enableConvoAISearch = false,
    retargetingEligibleUseCases,
  } = model;
  const initialTabTitle = requestedTab ?? tabDetails[0]?.tabName;

  const getUxlForDTL = useGetUxlForDTL();
  const retargetingPageViewId = useId();
  const theme = styleclass?.includes('inverse') ? 'dark' : 'light';
  const [dcaResponseByMbox, setDCAResponseByMbox] = useState<Record<string, unknown>>({});
  const pageModel = useDCAPageModelStore(state => state.pageModel) as Record<string, Record<string, unknown>[]>;
  const { datalayer, mvpOffers } = useSession();

  // Geo-targeting (gated by feature flag). The region is resolved entirely
  // client-side from the DataLayer state + regionStatesMapping (authored in
  // page.model.json). When the flag is off, region stays undefined and CF
  // requests behave exactly as before (default, non-regional content).
  const featureFlags = useClientEnvVarsStore(state => state.envVarsObject?.['featureFlags']) as unknown as
    | Record<string, unknown>
    | undefined;
  const isGeoTargetingEnabled = featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_HOMEPAGE_GEO_TARGETING] === true;
  const geoTargetingFlagRawValue = featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_HOMEPAGE_GEO_TARGETING];
  const regionStatesMapping = (pageModel?.['regionStatesMapping'] ?? undefined) as unknown as
    | Record<string, string[]>
    | undefined;
  // Reactive geo state so the CF fetch can (a) wait until geo is resolved and
  // (b) re-run when the resolved region changes. A plain ref would not trigger
  // the fetch effect, which caused UXL calls to fire with region=undefined
  // before the DataLayer state + regionStatesMapping had hydrated.
  const [resolvedGeoRegion, setResolvedGeoRegion] = useState<string | undefined>(undefined);
  // Becomes true once the region-resolution effect has produced a region for
  // the current inputs. When geo-targeting is off this stays false and is
  // unused (region is forced undefined at fetch time).
  const [isGeoResolved, setIsGeoResolved] = useState(false);
  useEffect(() => {
    console.log(
      `[GEO-TARGETING][CLIENT][DynamicTabbedLayout] isGeoTargetingEnabled: ${isGeoTargetingEnabled}, ` +
        `flagRawValue: ${JSON.stringify(geoTargetingFlagRawValue)}, hasRegionStatesMapping: ${!!regionStatesMapping}`
    );
    if (!isGeoTargetingEnabled) {
      setResolvedGeoRegion(undefined);
      setIsGeoResolved(false);
      return;
    }
    // Wait for the region inputs before resolving: we need the regionStatesMapping
    // (from pageModel) and the session datalayer to have hydrated. Until both are
    // present, keep geo "unresolved" so the CF fetch is deferred rather than
    // firing prematurely. Once they are present, resolve — even if the visitor
    // has no usable state the region stays undefined (no default region); the
    // fetch then requests only the `master` variation, which AEM serves as the
    // default. Either way geo is marked resolved so later tab switches aren't
    // blocked.
    if (!regionStatesMapping || !datalayer) {
      return;
    }
    const stateAbbr = getClientState(datalayer as Record<string, unknown> | undefined);
    // May be undefined when the state is unmapped — that's expected and means
    // "master default", not an error.
    const region = resolveRegionFromState(stateAbbr, regionStatesMapping);
    setResolvedGeoRegion(region);
    setIsGeoResolved(true);
    console.log(
      `[GEO-TARGETING][CLIENT][DynamicTabbedLayout] clientState: ${stateAbbr}, resolvedGeoRegion: ${
        region ?? '(none -> master default)'
      }`
    );
  }, [isGeoTargetingEnabled, geoTargetingFlagRawValue, datalayer, regionStatesMapping]);

  // build tab list (tabTitle is user-facing and used for hash / API)
  const tabItemList = useMemo(() => {
    const tabClickLocation = resolveTabClickLocation(trackingProperties?.trackingContentPosition);

    return tabDetails.map((tab, index) => ({
      tabValue: tab.nodeName || `tab-${index}`,
      tabTitle: tab.tabName,
      tabIcon: tab.tabIcon?.split(':')[1] ?? '',
      tabTrackingDescription: tab.tabTrackingDescription,
      dynamicMedia: tab.dynamicMedia,
      customTabClickTrackValue: `${tabClickLocation}|${buildTabTrackingPrefix(index + 1, tab.tabName)}|internal`,
      tabDCAtiles: tab.dcaTilesList ?? [],
      tabGeoTiles: isGeoTargetingEnabled ? tab.geoContentTilesList ?? [] : [],
      tabRetargetingTiles: tab.retargetingTilesList ?? [],
      tabPatternSlots: buildPatternSlots(
        tab.bannerFirst ?? false,
        tab.squareFirst ?? true,
        tab.dcaTilesList ?? [],
        isGeoTargetingEnabled ? tab.geoContentTilesList ?? [] : [],
        tab.retargetingTilesList ?? []
      ),
      tabDecisionScopes:
        (tab.dcaTilesList ?? []).map((dt: DcaNode) => dt.mboxParameter).filter((s): s is string => Boolean(s)) ?? [],
    }));
  }, [tabDetails, trackingProperties?.trackingContentPosition, isGeoTargetingEnabled]);

  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedTab, setSelectedTab] = useState<string>(requestedTab ?? tabItemList[0]?.tabTitle);
  const selectedTabCurrentNowRef = useRef<{ selectedTab: string | undefined; now: Date }>({
    selectedTab,
    now: new Date(),
  });
  if (selectedTabCurrentNowRef.current.selectedTab !== selectedTab) {
    selectedTabCurrentNowRef.current = { selectedTab, now: new Date() };
  }
  const retargetingCandidate = useRetargetingCandidate({
    now: selectedTabCurrentNowRef.current.now,
    eligibleUseCases: retargetingEligibleUseCases ?? RETARGETING_ELIGIBLE_USE_CASES,
    refreshKey: selectedTab,
    sessionDatalayer: datalayer,
  });
  useEffect(
    () => () => {
      clearRetargetingImageFailureLogs(retargetingPageViewId);
    },
    [retargetingPageViewId]
  );
  // tabSlots is a record of tab titles and their corresponding slots content
  const [tabSlots, setTabSlots] = useState<Record<string, SlotContent[]>>(() => {
    if (requestedTab && Array.isArray(requestedTabData)) {
      return { [requestedTab]: requestedTabData };
    }
    return {};
  });
  // tabSlotsRef is a ref to the tabSlots state
  // it is used to prevent re-rendering of the component when the tabSlots state changes
  const tabSlotsRef = useRef<Record<string, SlotContent[]>>(
    requestedTab && Array.isArray(requestedTabData) ? { [requestedTab]: requestedTabData } : {}
  );
  const secondaryPrefetchTriggeredRef = useRef(false);
  const secondaryPrefetchInFlightRef = useRef(false);
  useEffect(() => {
    tabSlotsRef.current = tabSlots;
  }, [tabSlots]);
  // Ref mirror for dcaResponseByMbox — keeps it out of useCallback deps to prevent
  // Effect C re-triggering (and duplicate impression fires) on secondary-tab mbox writes.
  const dcaResponseByMboxRef = useRef<Record<string, unknown>>(dcaResponseByMbox);
  useEffect(() => {
    dcaResponseByMboxRef.current = dcaResponseByMbox;
  }, [dcaResponseByMbox]);

  // Initial load: seed DCA responses from the global store (populated by useDCADynamicModel).
  // getDcaData is not called on initial load.
  useEffect(() => {
    if (pageModel?.['mboxData']?.length > 0 && Object.keys(dcaResponseByMbox).length === 0) {
      const tabMeta = tabItemList.find(t => t.tabTitle === selectedTab);
      const mboxes = tabMeta?.tabDecisionScopes ?? [];
      if (!mboxes.length) return;
      const mboxItems = (pageModel['mboxData'] as Record<string, unknown>[]).filter((item: Record<string, unknown>) => {
        const name = String(item?.['mboxName'] ?? '');
        return name && mboxes.includes(name);
      });
      const saveResponse = resolveMboxResponses(mboxItems, mboxes as string[]);
      if (Object.keys(saveResponse).length > 0) {
        setDCAResponseByMbox(saveResponse);
      }
    }
  }, [pageModel, tabItemList, selectedTab, dcaResponseByMbox]);

  const invokeAlloyFetch = useCallback(
    (payloadMboxes: IReturnTypeOFGetMboxParam[], options?: { onSettled?: () => void }) => {
      if (payloadMboxes.length === 0 || !datalayer) {
        options?.onSettled?.();
        return;
      }
      const alloyPromise = fetchDCADynamicModelForAlloy(payloadMboxes, {} as IPageModel, datalayer, mvpOffers);
      if (alloyPromise?.then) {
        void alloyPromise
          .then((response: unknown) => {
            const merged = mergeAlloyMboxResponses(payloadMboxes, response);
            setDCAResponseByMbox(prev => ({ ...prev, ...merged }));
          })
          .catch((err: unknown) => {
            console.warn('[invokeAlloyFetch] Alloy DCA fetch failed:', err);
            const merged = mergeAlloyMboxResponses(payloadMboxes, null);
            setDCAResponseByMbox(prev => ({ ...prev, ...merged }));
          })
          .finally(() => {
            options?.onSettled?.();
          });
      } else {
        options?.onSettled?.();
      }
    },
    [datalayer, mvpOffers]
  );

  const getDcaData = useCallback(() => {
    if (secondaryPrefetchInFlightRef.current) return;
    const tabMeta = tabItemList.find(t => t.tabTitle === selectedTab);
    const tabMboxesList = tabMeta?.tabDecisionScopes ?? [];
    const missingMboxes = getMissingMboxes(tabMboxesList, dcaResponseByMbox);

    if (missingMboxes.length === 0) {
      console.log(`[getDcaData] No missing mboxes, skipping fetch`);
      return;
    }

    const payloadMboxes = buildAlloyPayloadMboxes(missingMboxes, model);
    if (payloadMboxes.length === 0) return;

    invokeAlloyFetch(payloadMboxes);
  }, [model, selectedTab, tabItemList, dcaResponseByMbox, invokeAlloyFetch]);

  const prefetchSecondaryTabDca = useCallback(() => {
    if (secondaryPrefetchTriggeredRef.current) return;
    if (isAuthorMode || isMockDCADataEnabled()) {
      secondaryPrefetchTriggeredRef.current = true;
      return;
    }
    // Datalayer not yet available — return without locking the triggered ref so
    // the effect re-runs and retries once datalayer has loaded.
    if (!datalayer) {
      return;
    }
    const secondaryScopes = Array.from(
      new Set(tabItemList.filter(t => t.tabTitle !== initialTabTitle).flatMap(t => t.tabDecisionScopes ?? []))
    );

    if (secondaryScopes.length === 0) {
      secondaryPrefetchTriggeredRef.current = true;
      return;
    }

    // dcaResponseByMbox read via ref (same pattern as populateDcaFromAlloy) so this
    // callback stays stable across secondary-prefetch store updates — prevents Effect D
    // from re-running after the one-shot gate has already fired.
    const missingMboxes = getMissingMboxes(secondaryScopes, dcaResponseByMboxRef.current);
    if (missingMboxes.length === 0) {
      console.log(`[prefetchSecondaryTabDca] All secondary mboxes already fetched`);
      secondaryPrefetchTriggeredRef.current = true;
      return;
    }

    const payloadMboxes = buildAlloyPayloadMboxes(missingMboxes, model);
    if (payloadMboxes.length === 0) {
      console.warn('[prefetchSecondaryTabDca] Could not build Alloy payload for:', missingMboxes);
      return;
    }

    secondaryPrefetchTriggeredRef.current = true;
    secondaryPrefetchInFlightRef.current = true;

    invokeAlloyFetch(payloadMboxes, {
      onSettled: () => {
        secondaryPrefetchInFlightRef.current = false;
        console.log('[prefetchSecondaryTabDca] Secondary fetch settled');
      },
    });
    // dcaResponseByMbox intentionally omitted: read via dcaResponseByMboxRef.current above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datalayer, isAuthorMode, model, tabItemList, invokeAlloyFetch]);
  async function getCFDataFromApi(tabTitle: string, region?: string): Promise<CfNode[]> {
    try {
      // Execute UXL query using utility function
      const response = await getUxlForDTL(tabTitle, region);
      if (!response) {
        return [];
      }

      // Parse response - handle both success and error cases
      const aemContentResponse = response?.content?.aemContentByQueryName as
        | { __typename?: string; content?: string | unknown }
        | undefined;

      // Handle ResponseError case
      if (aemContentResponse?.__typename === 'ResponseError') {
        console.error('[getCFDataFromApi] ResponseError:', aemContentResponse);
        return [];
      }

      // Extract content from success response
      // The content field is a JSON string that needs to be parsed
      const contentString = aemContentResponse?.content;
      if (!contentString) {
        console.warn('[getCFDataFromApi] No content in response');
        return [];
      }

      let contentData;
      try {
        // Parse the JSON string content
        contentData = typeof contentString === 'string' ? JSON.parse(contentString) : contentString;
      } catch (parseError) {
        console.error('[getCFDataFromApi] Error parsing content JSON:', parseError);
        return [];
      }

      // Extract cards array from the parsed content.
      // The response contains one entry per CF variation (master + any
      // region-specific variation) under tabCollectionList.items. Pick the
      // cards for the resolved region, falling back to master, instead of
      // blindly taking the first node.
      const items = contentData?.data?.tabCollectionList?.items ?? contentData?.tabCollectionList?.items ?? [];
      const nodes = selectCardsForRegion<ExtendedCfNode>(items, region);

      // Normalize using existing utility function
      const normalized = normalizeCfItems(nodes, tabTitle);
      return normalized;
    } catch (e) {
      console.error('[getCFDataFromApi] Error calling UXL endpoint:', e);
      return [];
    }
  }
  // Ref to hold latest tabItemList so fetchTabData can stay stable (avoids effect loop when
  // parent passes new tabDetails reference on every render, e.g. from AEM ResponsiveGrid).
  const tabItemListRef = useRef(tabItemList);
  tabItemListRef.current = tabItemList;

  // fetch: server provides CF-filled slots + decisionScopes + slotsMeta
  const inFlightRef = useRef<Set<string>>(new Set());
  // eslint-disable-next-line react-hooks/exhaustive-deps -- tabItemList excluded: read from tabItemListRef to avoid effect loop when parent passes new tabDetails ref each render
  const fetchTabData = useCallback(
    async (tabTitle: string) => {
      if (tabSlotsRef.current[tabTitle]) return; // already loaded (e.g., SSR-provided)
      if (inFlightRef.current.has(tabTitle)) return;
      inFlightRef.current.add(tabTitle);

      try {
        const tabItemList = tabItemListRef.current;
        const tabMeta = tabItemList.find(t => t.tabTitle === tabTitle);
        if (!tabMeta) return;

        const region = isGeoTargetingEnabled ? resolvedGeoRegion : undefined;
        console.log(
          `[GEO-TARGETING][CLIENT][DynamicTabbedLayout] fetching CF for tab: ${tabTitle}, isGeoTargetingEnabled: ${isGeoTargetingEnabled}, region: ${region}, path: ${
            isAuthorMode || isMockCFDataEnabled() ? 'fetchCFData' : 'getCFDataFromApi'
          }`
        );
        const contentFragments =
          isAuthorMode || isMockCFDataEnabled()
            ? await fetchCFData(tabTitle, isAuthorMode, region)
            : await getCFDataFromApi(tabTitle, region);
        const mergedSlots = mergeCfAndDcaToSlots(
          tabMeta.tabPatternSlots || [],
          tabMeta.tabDCAtiles || [],
          contentFragments,
          // A client-side tab fetch already carries the resolved region in the CF
          // query (region + master), so geo-cf slots receive their region content
          // inline — no skeleton/second call needed. `isAuthenticated: true` marks
          // them as loaded (payload seeded) rather than pending.
          {
            geoNodes: tabMeta.tabGeoTiles || [],
            retargetingNodes: tabMeta.tabRetargetingTiles || [],
            isAuthenticated: true,
          }
        );

        setTabSlots(prev => {
          const next = { ...prev, [tabTitle]: mergedSlots || [] };
          return next;
        });
        // Do NOT call populateDcaFromAlloy here — Effect C (lines below) is the single
        // responsible path for DCA overlay. It runs whenever tabSlots OR dcaResponseByMbox
        // changes, so it correctly handles both orderings (CF first or DCA first) without
        // any partial-overlay side-effects.
      } catch (err) {
        console.error('Error fetching dynamic tab data', err);
      } finally {
        inFlightRef.current.delete(tabTitle);
      }
    },
    [getCFDataFromApi, isAuthorMode, isGeoTargetingEnabled, resolvedGeoRegion]
  );

  // DCA overlay (client-side). If NEXT_PUBLIC_USE_MOCK_DCADATA === 'true' use dcaMockData; else use store responses.
  const populateDcaFromAlloy = useCallback(
    async (tabTitle: string, dcaTiles?: DcaNode[], _tabDecisionScopes?: string[], slotsFromCaller?: SlotContent[]) => {
      const useMock = isMockDCADataEnabled();
      try {
        if (!dcaTiles?.length) return;

        const orderByMbox = new Map<string, number>();
        for (const t of dcaTiles) {
          const mboxParam = t?.mboxParameter;
          const ord = Number(t?.order);
          if (mboxParam && !Number.isNaN(ord)) orderByMbox.set(mboxParam, ord);
        }

        const responseByMbox = useMock ? (dcaMockData as Record<string, unknown>) : dcaResponseByMboxRef.current ?? {};
        const dcaNodes: DcaNode[] = dcaTiles
          .map(tile => {
            const mbox = tile?.mboxParameter;
            if (!mbox) return null;
            const payload = responseByMbox[mbox];
            if (!payload) return null;
            return {
              mboxParameter: mbox,
              order: orderByMbox.get(mbox),
              payload,
            } as DcaNode;
          })
          .filter((n): n is DcaNode => Boolean(n));

        if (!slotsFromCaller?.length) return;
        setTabSlots(prev => {
          // Overlay against the latest committed slots (prev), not the captured
          // snapshot, so a concurrent geo overlay isn't clobbered/reverted.
          const prevSlots = prev[tabTitle] ?? slotsFromCaller;
          const updated = overlaySlotsWithDcaRes(prevSlots, dcaNodes, { fillFromCFFallbackIfEmpty: useMock });
          if (isSameTabSlotArray(prevSlots, updated as SlotContent[])) return prev;
          return { ...prev, [tabTitle]: updated as SlotContent[] };
        });
      } catch (e) {
        console.warn('[populateDcaFromAlloy] DCA overlay failed, using cfFallback for DCA slots', e);
        if (slotsFromCaller?.length) {
          setTabSlots(prev => {
            const prevSlots = prev[tabTitle] ?? slotsFromCaller;
            const updated = overlaySlotsWithDcaRes(prevSlots, [], { fillFromCFFallbackIfEmpty: true });
            if (isSameTabSlotArray(prevSlots, updated as SlotContent[])) return prev;
            return { ...prev, [tabTitle]: updated as SlotContent[] };
          });
        }
      }
    },
    // dcaResponseByMbox intentionally omitted: read via dcaResponseByMboxRef.current so this
    // function stays stable across secondary-prefetch store updates (prevents Effect C re-runs).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    // Mark hydration complete; data from SSR is already present in state
    setIsHydrated(true);
  }, []);

  // If SSR provided requestedTabData, attempt to overlay with available DCA responses after hydration
  // (restricted to the SSR tab only, not the client-selected tab).
  useEffect(() => {
    if (!isHydrated) return; // wait for hydration to complete
    if (!requestedTab || !requestedTabData) return; // no requested tab data
    const tabMeta = tabItemList.find(t => t.tabTitle === requestedTab);
    if (!tabMeta) return;
    const slots = Array.isArray(requestedTabData) ? requestedTabData : [];
    void populateDcaFromAlloy(
      requestedTab,
      tabMeta.tabDCAtiles,
      tabMeta.tabDecisionScopes as string[],
      slots as SlotContent[]
    );
  }, [isHydrated, requestedTab, requestedTabData, tabItemList, populateDcaFromAlloy]);

  // Geo-cf overlay (client-side). Fires the region+master UXL call for a tab that
  // has reserved geo-cf slots waiting (isLoading === true, i.e. logged-out SSR
  // first paint where only the master was seeded + skeletons shown). Region cards
  // fill the slots; slots with no usable region content fall back to the seeded
  // master (`cfFallback`). When geo-targeting is off no geo-cf slots exist, so this
  // is a no-op. Logged-in slots arrive pre-seeded (isLoading === false) and skip it.
  const geoFilledTabsRef = useRef<Set<string>>(new Set());
  const populateGeoForTab = useCallback(
    async (tabTitle: string, slotsFromCaller?: SlotContent[]) => {
      try {
        if (!isGeoTargetingEnabled) return;
        if (!slotsFromCaller?.length) return;
        // Only act when there is at least one geo-cf slot still awaiting region content.
        const hasPending = slotsFromCaller.some(s => s?.type === 'geo-cf' && s?.isLoading === true);
        if (!hasPending) return;
        if (geoFilledTabsRef.current.has(tabTitle)) return;
        geoFilledTabsRef.current.add(tabTitle);

        const region = resolvedGeoRegion;
        console.log(
          `[GEO-TARGETING][CLIENT][DynamicTabbedLayout] geo fill for tab: ${tabTitle}, region: ${
            region ?? '(none -> master fallback)'
          }`
        );
        // Region + master call. AEM serves the region variation when present, else
        // master; the seeded master already lives on each slot as cfFallback.
        const regionCf =
          isAuthorMode || isMockCFDataEnabled()
            ? await fetchCFData(tabTitle, isAuthorMode, region)
            : await getCFDataFromApi(tabTitle, region);

        // Map each geo-cf slot's grid index to its region CF node. The CF array is
        // indexed by grid position (same convention mergeCfAndDcaToSlots uses).
        const geoNodes: GeoNode[] = [];
        for (let i = 0; i < slotsFromCaller.length; i++) {
          const s = slotsFromCaller[i];
          if (s?.type !== 'geo-cf') continue;
          geoNodes.push({ order: s.position, payload: regionCf?.[i] ?? null });
        }

        setTabSlots(prev => {
          const prevSlots = prev[tabTitle];
          // Apply the geo settle against the latest committed slots (prev) so a
          // concurrent DCA overlay isn't clobbered/reverted. Fall back to the
          // captured snapshot when prev has no entry yet. geoNodes are keyed by
          // slot position, so applying to prev (same positions) is safe.
          const base = prevSlots ?? slotsFromCaller;
          const mergedUpdated = overlaySlotsWithGeoRes(base, geoNodes, { settled: true });
          if (isSameTabSlotArray(prevSlots, mergedUpdated)) return prev;
          return { ...prev, [tabTitle]: mergedUpdated };
        });
      } catch (e) {
        console.warn('[populateGeoForTab] geo overlay failed, using master cfFallback for geo-cf slots', e);
        if (slotsFromCaller?.length) {
          // Settle everything to the seeded master so skeletons don't hang.
          setTabSlots(prev => {
            const prevSlots = prev[tabTitle];
            const base = prevSlots ?? slotsFromCaller;
            const updated = overlaySlotsWithGeoRes(base, [], { settled: true });
            if (isSameTabSlotArray(prevSlots, updated)) return prev;
            return { ...prev, [tabTitle]: updated };
          });
        }
      }
    },
    [isGeoTargetingEnabled, resolvedGeoRegion, isAuthorMode]
  );

  // Drive the geo fill for the SSR-seeded tab once hydrated + region resolved.
  useEffect(() => {
    if (!isHydrated) return;
    if (!isGeoTargetingEnabled || !isGeoResolved) return;
    if (!requestedTab) return;
    const tabMeta = tabItemList.find(t => t.tabTitle === requestedTab);
    if (!tabMeta) return;
    const slots = tabSlotsRef.current[requestedTab];
    if (!Array.isArray(slots) || !slots.length) return;
    void populateGeoForTab(requestedTab, slots as SlotContent[]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- read slots from tabSlotsRef to avoid re-run loops on every tabSlots change
  }, [isHydrated, isGeoTargetingEnabled, isGeoResolved, requestedTab, resolvedGeoRegion, populateGeoForTab]);

  // Fetch when selectedTab changes (client-side effect; SSR never runs effects).
  useEffect(() => {
    if (!selectedTab) return;
    // When geo-targeting is on, defer the fetch until the region is resolved so
    // the UXL call carries a region instead of undefined. The requested tab is
    // seeded from SSR (already region-aware), so its guard short-circuits and
    // this only gates tabs the user switches to. Once resolvedGeoRegion lands,
    // fetchTabData is re-created (region in deps) and this effect re-runs.
    if (isGeoTargetingEnabled && !isGeoResolved) return;
    void fetchTabData(selectedTab);
  }, [selectedTab, fetchTabData, isGeoTargetingEnabled, isGeoResolved]);

  // On tab changes (not initial load), fetch missing DCA data for that tab.
  // Use a previous-tab guard (instead of "skip first run") to avoid React StrictMode double-effect
  // invoking `getDcaData` on initial hydration.
  const prevSelectedTabRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isHydrated) return;
    if (!selectedTab) return;

    const prev = prevSelectedTabRef.current;
    prevSelectedTabRef.current = selectedTab;

    // First hydrated run: just initialize prev
    if (prev == null) return;
    // No actual tab change (e.g. StrictMode re-run): do nothing
    if (prev === selectedTab) return;

    getDcaData();
  }, [isHydrated, selectedTab, getDcaData]);

  // Serializes only the current tab's mbox response values into a stable string.
  // Used as the dependency for Effect C instead of the full dcaResponseByMbox object so that
  // secondary-prefetch writes for OTHER tabs do not trigger an extra DCA overlay (and a duplicate
  // impression fire) for the currently visible tab.
  const selectedTabMboxSnapshot = useMemo(() => {
    const tabMeta = tabItemList.find(t => t.tabTitle === selectedTab);
    const scopes = tabMeta?.tabDecisionScopes ?? [];
    return JSON.stringify(scopes.map(s => dcaResponseByMbox[s] ?? null));
  }, [tabItemList, selectedTab, dcaResponseByMbox]);

  // When DCA responses arrive, overlay them onto already-loaded slots (avoid loops).
  useEffect(() => {
    if (!isHydrated) return;
    if (!selectedTab) return;
    const slots = tabSlots[selectedTab];
    if (!Array.isArray(slots) || slots.length === 0) return;
    const tabMeta = tabItemList.find(t => t.tabTitle === selectedTab);
    if (!tabMeta) return;

    // Collect all DCA slots that still need a response applied.
    const pendingDcaSlots = slots.filter(s => {
      if (s?.type !== 'dca') return false;
      const p = s?.payload;
      // Already has a good payload — nothing to do.
      if (p !== null && p !== undefined && !isDcaTargetPayloadFailed(p)) return false;
      return true;
    });

    if (pendingDcaSlots.length === 0) return;

    // Only proceed once every pending DCA slot has a response (even an empty/failed one).
    // This prevents a partial overlay caused by shared mboxes from other tabs being present
    // in dcaResponseByMbox before this tab's own DCA call has finished.
    const allPendingResolved = pendingDcaSlots.every(s => {
      const mbox = s?.dcaTarget?.mboxParameter;
      return Boolean(mbox && dcaResponseByMbox?.[mbox]);
    });

    if (!allPendingResolved) return;

    void populateDcaFromAlloy(
      selectedTab,
      tabMeta.tabDCAtiles,
      tabMeta.tabDecisionScopes as string[],
      slots as SlotContent[]
    );
    // selectedTabMboxSnapshot (not raw dcaResponseByMbox) is used as the dep so this effect only
    // re-runs when the CURRENT tab's mbox values change. Secondary-prefetch writes for other tabs
    // do not change the snapshot and therefore do not trigger a redundant overlay + impression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHydrated, selectedTab, tabSlots, tabItemList, selectedTabMboxSnapshot, populateDcaFromAlloy]);

  // After primary tab CF + DCA overlay is complete, batch-fetch all secondary tab mboxes once.
  // When the primary tab has no mboxes, proceed immediately after
  // hydration — no DCA readiness signal will ever arrive for that tab.
  // datalayer is included as a dependency so that if the effect fires before the
  // datalayer has loaded (prefetchSecondaryTabDca returns early without locking the
  // triggered ref), the effect re-runs once datalayer becomes available and retries.
  useEffect(() => {
    if (!isHydrated) return;
    if (!datalayer) return;
    if (secondaryPrefetchTriggeredRef.current) return;

    if (!initialTabTitle) return;

    const primaryMeta = tabItemList.find(t => t.tabTitle === initialTabTitle);
    if (!primaryMeta) return;

    const primaryScopes = primaryMeta.tabDecisionScopes ?? [];
    const primaryHasNoMboxes = primaryScopes.length === 0;

    const primarySlots = tabSlots[initialTabTitle];

    // When primary tab has DCA mboxes, wait for CF slots to populate first.
    // When it has no mboxes, no DCA readiness
    // signal will ever arrive — proceed immediately after hydration.
    if (!primaryHasNoMboxes && (!Array.isArray(primarySlots) || primarySlots.length === 0)) return;

    const primaryDcaSeeded =
      primaryHasNoMboxes || primaryScopes.every(scope => scope && dcaResponseByMbox[scope] != null);
    if (!primaryDcaSeeded) return;

    if (!primaryHasNoMboxes && Array.isArray(primarySlots)) {
      const pendingDcaSlots = primarySlots.filter(s => {
        if (s?.type !== 'dca') return false;
        const p = s?.payload;
        if (p !== null && p !== undefined && !isDcaTargetPayloadFailed(p)) return false;
        return true;
      });

      if (pendingDcaSlots.length > 0) {
        const allPendingResolved = pendingDcaSlots.every(s => {
          const mbox = s?.dcaTarget?.mboxParameter;
          return Boolean(mbox && dcaResponseByMbox?.[mbox]);
        });
        if (!allPendingResolved) return;
      }
    }

    prefetchSecondaryTabDca();
  }, [isHydrated, datalayer, tabSlots, dcaResponseByMbox, tabItemList, prefetchSecondaryTabDca]);

  // Sync URL with selectedTab on hydration — handles edge cases where the URL
  // still contains a stale/invalid tab value (e.g. browser restored from bfcache).
  useEffect(() => {
    if (!isHydrated) return;
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    // If URL tab doesn't match selected tab, silently correct the URL
    if (tabParam && tabParam !== selectedTab) {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', selectedTab);
      window.history.replaceState(null, '', url.toString());
    }
  }, [isHydrated, selectedTab]);

  // sync query param (?tab=) changes via browser navigation -> selectedTab
  useEffect(() => {
    const onPopState = () => {
      const params = new URLSearchParams(window.location.search);
      const tabParam = params.get('tab');
      const firstTab = tabItemList[0]?.tabTitle;

      // No tab param — default to first tab and correct the URL
      if (!tabParam) {
        if (firstTab) {
          startTransition(() => setSelectedTab(firstTab));
          const url = new URL(window.location.href);
          url.searchParams.set('tab', firstTab);
          window.history.replaceState(null, '', url.toString());
        }
        return;
      }

      // Valid tab — select it (case-insensitive match)
      const matched = tabItemList.find(tab => tab.tabTitle.toLowerCase() === tabParam.toLowerCase());
      if (matched) {
        startTransition(() => setSelectedTab(matched.tabTitle));
      } else if (firstTab) {
        // Invalid tab — default to first tab and correct the URL
        startTransition(() => setSelectedTab(firstTab));
        const url = new URL(window.location.href);
        url.searchParams.set('tab', firstTab);
        window.history.replaceState(null, '', url.toString());
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [tabItemList]);

  // click handler: update selectedTab (fetchTabData runs via effect)
  const onTabSelect = useCallback((tabTitle: string) => {
    startTransition(() => setSelectedTab(tabTitle));
    const url = new URL(window.location.href);
    url.searchParams.set('tab', tabTitle);
    window.history.pushState(null, '', `${url.pathname}?${url.searchParams.toString()}`);
  }, []);

  // selectedTabMeta is the metadata for the selected tab which includes the tab title, dynamic media, and pattern slots
  const selectedTabMeta = useMemo(() => tabItemList.find(t => t.tabTitle === selectedTab), [tabItemList, selectedTab]);
  const selectedTabDynamicMedia = useMemo(
    () => selectedTabMeta?.dynamicMedia ?? (tabItemList[0]?.dynamicMedia as renditions),
    [selectedTabMeta, tabItemList]
  );

  const selectedTabIndex = useMemo(() => {
    const rawTabIndex = tabItemList.findIndex(t => t.tabTitle === selectedTab);
    return rawTabIndex >= 0 ? rawTabIndex : 0;
  }, [tabItemList, selectedTab]);
  // currentSlots is the slots content for the selected tab
  const currentSlots = useMemo(() => tabSlots[selectedTab] ?? [], [tabSlots, selectedTab]);
  // panelSlots is the slots content for the selected tab
  const panelSlots = useMemo(() => {
    const patternSlots = selectedTabMeta?.tabPatternSlots ?? [];
    if (!patternSlots.length) return currentSlots;
    if (!currentSlots.length) return patternSlots as SlotContent[];

    const slotsByPosition = new Map<number, SlotContent>();
    currentSlots.forEach(slot => {
      if (typeof slot?.position === 'number') slotsByPosition.set(slot.position, slot);
    });

    return patternSlots.map(pattern => {
      const match = slotsByPosition.get(pattern.position);
      if (!match) return pattern as SlotContent;
      // Pattern carries grid meta (size, position, authored type). Loaded `match` has overlay state
      // (type/payload after DCA or CF fallback) and must win so we do not reset promoted `cf` slots back to `dca`.
      return { ...pattern, ...match } as SlotContent;
    });
  }, [selectedTabMeta, currentSlots]);

  const effectivePanelSlots = useMemo(() => {
    if (!panelSlots.length) return panelSlots;
    if (!isHydrated) return panelSlots;
    const tabName = tabItemList[selectedTabIndex]?.tabTitle || '';
    const oneBasedTabIndex = selectedTabIndex + 1;
    const tabTrackingPosition = buildTabTrackingPrefix(oneBasedTabIndex, tabName);

    return panelSlots.map(slot => {
      if (!slot?.retargetingTarget) return slot;
      const size = (slot?.size as 'square' | 'tall' | 'banner') ?? 'square';
      return resolveRetargetingSlot(
        slot,
        retargetingCandidate,
        {
          index: slot.position,
          tabIndex: oneBasedTabIndex,
          tabName,
          slotSize: size,
          trackingProperties: {
            ...trackingProperties,
            tabTrackingContentPosition: tabTrackingPosition,
          },
        },
        retargetingPageViewId
      );
    });
  }, [
    panelSlots,
    isHydrated,
    selectedTabIndex,
    tabItemList,
    retargetingCandidate,
    trackingProperties,
    retargetingPageViewId,
  ]);

  const slotItems = useMemo(() => {
    const tabName = tabItemList[selectedTabIndex]?.tabTitle || '';
    const oneBasedTabIndex = selectedTabIndex + 1;
    const tabTrackingPosition = buildTabTrackingPrefix(oneBasedTabIndex, tabName);
    let cardIndex = 0;

    return (effectivePanelSlots || []).map(slot => {
      // Include tab in the key so switching tabs remounts slot cards.
      const key = `tab-${oneBasedTabIndex}-slot-${slot?.position ?? 0}`;
      const size = (slot?.size as 'square' | 'tall' | 'banner') ?? 'square';
      const isBanner = size === 'banner';
      const currentCardIndex = isBanner ? 0 : cardIndex++;
      const colClass = isBanner ? '' : `pos-${(currentCardIndex % 3) + 1}`;
      const trackingCtx = {
        index: slot?.position ?? 0,
        tabIndex: oneBasedTabIndex,
        tabName,
        slotSize: size,
        trackingProperties: {
          ...trackingProperties,
          tabTrackingContentPosition: tabTrackingPosition,
        },
      };
      return (
        <StyledTabPanelItem
          key={key}
          className={clsx(size, colClass, `slot-${slot?.position ?? 0}`, `${slot?.type}-slot`)}
        >
          {slot?.type === 'retargeting' ? (
            <RetargetingSlotErrorBoundary
              resetKey={`${selectedTab}-${slot?.position ?? 0}-${slot?.type ?? 'cf'}-${
                retargetingCandidate?.timestamp ?? 'no-candidate'
              }`}
              slot={slot}
              trackingCtx={trackingCtx}
            />
          ) : (
            resolveSlotComponent(slot, trackingCtx)
          )}
        </StyledTabPanelItem>
      );
    });
  }, [effectivePanelSlots, tabItemList, selectedTab, selectedTabIndex, trackingProperties, retargetingCandidate]);

  // WEB-210419 / tab-switch fix.
  // Non-cobrand DCA merchandising impressions (event168|list3) are sent by an Adobe Launch rule that
  // reads window.atMArray (each card populates it via getClickTrackValue when its authored
  // `impressionTrack` is true). Runtime behavior:
  //   - INITIAL LOAD: the Launch page-load "fireMarketing" flow triggers once and reads atMArray, so
  //     exactly one event168|list3 fires. A parallel DTL emitter here caused the WEB-210419 DOUBLE, so
  //     the DTL MUST stay silent on initial load.
  //   - TAB SWITCH: atMArray is repopulated with the new tab's placements, but the Launch page-load
  //     flow does NOT re-run, so nothing flushes atMArray -> non-cobrand impressions stopped firing
  //     (cobrand still fired because those call impressionTrack() directly per card).
  // Fix: fire the DTL non-cobrand list3 impression ONLY on a genuine tab CHANGE, exactly once per
  // visit, after the async mbox-by-mbox entry list settles (debounced). Initial load -> Launch
  // page-load path (single). Tab switch -> this effect provides the single trigger (Launch does not
  // re-run). Each tab view => exactly one non-cobrand list3.
  const nonCobrandDcaImpressionEntries = useMemo(() => {
    const tabName = tabItemList[selectedTabIndex]?.tabTitle || '';
    const oneBasedTabIndex = selectedTabIndex + 1;
    const tabTrackingPosition = buildTabTrackingPrefix(oneBasedTabIndex, tabName);
    const entries = (effectivePanelSlots || [])
      .map(slot =>
        getDcaNonCobrandImpressionEntry(slot, {
          index: slot?.position ?? 0,
          tabIndex: oneBasedTabIndex,
          tabName,
          slotSize: (slot?.size as 'square' | 'tall' | 'banner') ?? 'square',
          trackingProperties: {
            tabTrackingContentPosition: tabTrackingPosition,
          },
        })
      )
      .filter((entry): entry is string => Boolean(entry));

    return Array.from(new Set(entries));
  }, [effectivePanelSlots, tabItemList, selectedTabIndex]);

  // Tab present at mount. The initial tab's first visit is owned by the Adobe Launch page-load flow
  // (it reads window.atMArray and fires the single combined list3), so the DTL must NEVER emit for it
  // on load — not on the first effect run, and not on later runs as the async mbox list keeps growing.
  // `undefined` until seeded on the first effect run.
  const initialNonCobrandTabRef = useRef<string | undefined>(undefined);
  const initialNonCobrandSeededRef = useRef(false);
  // The tab for which we have ALREADY emitted the combined DTL list3 during this visit. Prevents
  // re-emitting for the same tab when the async mbox-by-mbox resolution keeps growing the entry list.
  const firedNonCobrandImpressionTabRef = useRef<string | null>(null);
  // Debounce handle so the growing (async-resolving) entry list collapses into ONE combined call.
  const nonCobrandImpressionTimerRef = useRef<number | undefined>(undefined);
  // How long to wait after the entry list stops changing before firing the single combined call.
  const NON_COBRAND_IMPRESSION_SETTLE_MS = 700;

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Seed the initial tab exactly once. Its first visit is covered by the Launch page-load flow.
    if (!initialNonCobrandSeededRef.current) {
      initialNonCobrandSeededRef.current = true;
      initialNonCobrandTabRef.current = selectedTab;
    }

    // Initial tab's FIRST visit: the Launch page-load flow fires the single combined list3. Firing
    // here would double-count (WEB-210419). This must hold across ALL renders of the initial visit,
    // not just the first — the async entry growth on load must never trigger a DTL emit. Once the
    // user switches away and returns, `firedNonCobrandImpressionTabRef` governs the revisit instead.
    const isInitialTabFirstVisit =
      selectedTab === initialNonCobrandTabRef.current && firedNonCobrandImpressionTabRef.current === null;
    if (isInitialTabFirstVisit) return;

    // Already emitted the single combined call for this tab visit — ignore further async entry growth.
    if (firedNonCobrandImpressionTabRef.current === selectedTab) return;
    if (!nonCobrandDcaImpressionEntries.length) return;

    // Content resolves mbox-by-mbox, so this effect re-runs with a growing entry list. Debounce and
    // fire ONE combined list3 once the list settles, then mark this tab as fired for the visit.
    const tabAtScheduleTime = selectedTab;
    window.clearTimeout(nonCobrandImpressionTimerRef.current);
    nonCobrandImpressionTimerRef.current = window.setTimeout(() => {
      // Tab changed again before the timer fired — let the newer run own the emit.
      if (tabAtScheduleTime !== selectedTab) return;
      if (firedNonCobrandImpressionTabRef.current === selectedTab) return;

      const impressionTrack = (window as unknown as { impressionTrack?: (value: string) => void }).impressionTrack;
      if (typeof impressionTrack !== 'function') return;

      const impressionValue = `event168|list3=${nonCobrandDcaImpressionEntries.join('^')}`;
      impressionTrack(impressionValue);
      firedNonCobrandImpressionTabRef.current = selectedTab ?? null;
    }, NON_COBRAND_IMPRESSION_SETTLE_MS);

    return () => window.clearTimeout(nonCobrandImpressionTimerRef.current);
  }, [nonCobrandDcaImpressionEntries, selectedTab]);

  return (
    <StyledDynamicTabbedLayout
      className={clsx('m-container-fullbleed', styleclass)}
      data-target-component={targetComponentName || null}
      data-testid="homepage-DynamicTabbedLayout"
      data-component-name="o-homepage-DynamicTabbedLayout"
    >
      <TabBanner theme={theme} dynamicMedia={selectedTabDynamicMedia} />

      <TabNavigation
        selectedTab={selectedTab}
        setSelectedTab={onTabSelect}
        tabNavigationList={tabItemList}
        pageTitle={pageTitle}
        isNextGenSearchForm={isNextGenSearchForm}
        isConvoAIActive={enableConvoAISearch}
      >
        <div id={`${selectedTab}-panel`} role="tabpanel" aria-labelledby={selectedTabMeta?.tabValue || ''}>
          <StyledTabPanelGrid>{slotItems}</StyledTabPanelGrid>
        </div>
      </TabNavigation>
    </StyledDynamicTabbedLayout>
  );
};
